import { useCallback, useEffect, useState } from 'react'
import { Filter, RefreshCw, AlertTriangle } from 'lucide-react'
import { fetchAdminFunnel } from '../services/adminService'
import type { AdminFunnel, FunnelStage } from '../types'

/**
 * The resident funnel: how many people got how far, and where they were lost.
 *
 * This exists because for five months the only way to learn that nobody had
 * booked a pickup — or that the offer engine had never run — was to query the
 * production database by hand. Seven stages, each dated by its own event, with
 * the biggest loss highlighted and a one-line verdict above the bars.
 *
 * It renders zeros. Deliberately and prominently: a funnel of zeros looks like
 * a loading state, and the verdict line is there to say it is not.
 */

type Days = 7 | 30 | 90

const WINDOWS: Days[] = [7, 30, 90]

/** Payment stages are pay-per-pickup only; subscribers skip them. */
const PAYMENT_STAGES = new Set(['paymentAttempted', 'paymentSucceeded'])

export default function FunnelCard() {
  const [days, setDays] = useState<Days>(30)
  const [funnel, setFunnel] = useState<AdminFunnel | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const data = await fetchAdminFunnel(days)
      setFunnel(data ?? null)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    load()
  }, [load])

  const top = funnel?.stages[0]?.count ?? 0

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Filter size={16} className="text-primary-600 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-[#1A1A2E]">Resident funnel</h3>
            <p className="text-[11px] text-[#6B7280]">
              From signing up to a bin being emptied, last {days} days
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex bg-gray-100 rounded-xl overflow-hidden">
            {WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setDays(w)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  days === w ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-200'
                }`}
                aria-pressed={days === w}
              >
                {w}d
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-[#1A1A2E] transition-colors"
            title="Refresh"
            aria-label="Refresh funnel"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {error ? (
        <div className="px-5 py-8 text-center text-sm text-red-600">Could not load the funnel.</div>
      ) : loading && !funnel ? (
        <div className="px-5 py-8 text-center text-sm text-[#9CA3AF]">Loading…</div>
      ) : funnel ? (
        <div className="px-5 py-4 space-y-4">
          {/* The verdict comes first. With a row of zeros below it, this line is
              the difference between "broken widget" and "here is your problem". */}
          <p
            className={`text-sm font-semibold rounded-xl px-3 py-2 ${
              funnel.stages.find((s) => s.key === 'completed')?.count
                ? 'bg-green-50 text-green-800'
                : 'bg-amber-50 text-amber-900'
            }`}
          >
            {funnel.verdict}
          </p>

          <div className="space-y-2">
            {funnel.stages.map((stage, i) => (
              <StageRow
                key={stage.key}
                stage={stage}
                top={top}
                isBiggestDrop={funnel.biggestDrop?.key === stage.key}
                lost={funnel.biggestDrop?.key === stage.key ? funnel.biggestDrop.lost : null}
                isFirst={i === 0}
              />
            ))}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-gray-100">
            <p className="text-[10px] text-[#9CA3AF]">
              "Tried to pay" and "Paid" count pay-per-pickup only — subscribers skip them, so a rise
              after them is healthy.
            </p>
            <div className="flex items-center gap-2">
              <Chip label="waiting on a collector" value={funnel.now.pendingUnassigned} />
              <Chip label="waiting on an admin" value={funnel.now.escalated} warn={funnel.now.escalated > 0} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

interface StageRowProps {
  stage: FunnelStage
  top: number
  isBiggestDrop: boolean
  lost: number | null
  isFirst: boolean
}

function StageRow({ stage, top, isBiggestDrop, lost, isFirst }: StageRowProps) {
  // Bars are scaled to the first stage, so every bar is "share of everyone who
  // signed up" — the question an operator is actually asking.
  const width = top > 0 ? Math.max(stage.count > 0 ? 2 : 0, (stage.count / top) * 100) : 0
  const payment = PAYMENT_STAGES.has(stage.key)

  return (
    <div className={`rounded-xl px-3 py-2 ${isBiggestDrop ? 'bg-red-50 border border-red-200' : ''}`}>
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {isBiggestDrop && <AlertTriangle size={12} className="text-red-600 flex-shrink-0" />}
          <span className={`text-xs font-medium truncate ${payment ? 'text-[#6B7280]' : 'text-[#1A1A2E]'}`}>
            {stage.label}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 text-xs">
          {!isFirst && stage.dropPercent != null && stage.dropPercent > 0 && (
            <span className={isBiggestDrop ? 'text-red-700 font-semibold' : 'text-[#9CA3AF]'}>
              −{stage.dropPercent}%{lost != null ? ` · lost ${lost}` : ''}
            </span>
          )}
          {!isFirst && stage.ratio != null && stage.ratio > 1 && (
            <span className="text-green-700" title="More than the previous stage — subscribers skip payment">
              +{Math.round((stage.ratio - 1) * 100)}%
            </span>
          )}
          <span className="font-bold text-[#1A1A2E] tabular-nums w-10 text-right">{stage.count}</span>
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-2 rounded-full transition-all ${
            isBiggestDrop ? 'bg-red-500' : payment ? 'bg-gray-400' : 'bg-primary-600'
          }`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  )
}

function Chip({ label, value, warn = false }: { label: string; value: number; warn?: boolean }) {
  return (
    <span
      className={`text-[10px] font-semibold px-2 py-1 rounded-lg ${
        warn ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-[#6B7280]'
      }`}
    >
      {value} {label}
    </span>
  )
}
